import { useState, useEffect } from "react";
import axios from "axios";
import {
  DataGridPremium,
  GridColDef,
  GridInitialState,
  GridRowsProp,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
  GridApiPremium,
} from "@mui/x-data-grid-premium";
import { Box, Alert, Paper, TablePagination } from "@mui/material";
import { CustomColumnMenu } from "../ColumnMenu";

export interface DetailPanelComponentProps {
  params: GridRowParams;
  apiEndpoint: string;
  columns?: GridColDef[];
  initialStateOverride?: GridInitialState;
  nestedRelationName: string;
  getNestedRelationKey?: (params: GridRowParams) => string | string[];
  getRowId?: (row: GridValidRowModel) => string | number;
  parentApiRef?: React.RefObject<GridApiPremium>;
  cache?: Map<string, { rows: GridRowsProp; totalCount: number }>;
}

const apiBase = process.env.REACT_APP_BACKEND_API_URL;

// Helper function to convert relation key to cache key
export const getCacheKey = (key: string | string[] | number): string => {
  if (Array.isArray(key)) {
    return key.join("|");
  }
  return String(key);
};

export const NestedSubtableDetailPanel: React.FC<DetailPanelComponentProps> = ({
  params,
  apiEndpoint,
  columns,
  nestedRelationName,
  getNestedRelationKey = (params: GridRowParams) => params.id,
  initialStateOverride,
  getRowId = (row: GridValidRowModel) => row.id,
  parentApiRef,
  cache,
}) => {
  // Validate that if cache is provided, parentApiRef must also be provided
  if (cache && !parentApiRef) {
    throw new Error(
      "parentApiRef is required when cache is provided in NestedSubtableDetailPanel"
    );
  }

  const apiRef = useGridApiRef();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(
    initialStateOverride?.sorting?.sortModel ?? []
  );
  const [data, setData] = useState<GridRowsProp>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-size columns when rows are loaded/updated
  useEffect(() => {
    if (!apiRef.current) return;

    const handleRowsSet = () => {
      // Rows have been set/updated, now we can safely autosize
      if (apiRef.current) {
        // Use requestAnimationFrame to ensure DOM has been painted before autosizing
        requestAnimationFrame(() => {
          if (apiRef.current) {
            const columnsToAutosize = apiRef.current
              .getAllColumns()
              .filter((col: any) => !col.disableAutosize)
              .map((col: any) => col.field);
            apiRef.current.autosizeColumns({
              includeHeaders: true,
              includeOutliers: true,
              expand: false, // Allow horizontal scroll when columns exceed container width
              columns: columnsToAutosize,
            });
          }
        });
      }
    };

    // Subscribe to the rowsSet event
    const unsubscribe = apiRef.current.subscribeEvent("rowsSet", handleRowsSet);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [apiRef]);

  useEffect(() => {
    const fetchNestedData = async () => {
      const relationKey = getNestedRelationKey(params);
      const sortKey = sortModel.map((s) => `${s.field}:${s.sort}`).join(",");
      const cacheKey = `${getCacheKey(relationKey)}|p${paginationModel.page}|s${
        paginationModel.pageSize
      }|sort:${sortKey}`;

      // Check if cache exists and has data
      if (cache) {
        const cachedData = cache.get(cacheKey);

        if (cachedData !== undefined) {
          // Use cached data
          setData(cachedData.rows);
          setRowCount(cachedData.totalCount);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${apiBase}/${apiEndpoint}`;
        const requestBody = {
          operation: "NESTED_READ",
          parentId: relationKey,
          relationName: nestedRelationName,
          gridRequest: {
            paginationModel,
            sortModel,
          },
        };

        const response = await axios.post(apiUrl, requestBody, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        const fetchedData = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        const fetchedTotal: number = response.data.totalCount ?? 0;

        setData(fetchedData);
        setRowCount(fetchedTotal);

        // Store in cache if cache is provided
        if (cache) {
          cache.set(cacheKey, { rows: fetchedData, totalCount: fetchedTotal });
        }
      } catch (err) {
        const errorMessage = axios.isAxiosError(err)
          ? err.response?.data?.message || err.message
          : err instanceof Error
          ? err.message
          : "Failed to fetch nested data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchNestedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sortModel]);

  // Register cache clearing callback with parent apiRef
  useEffect(() => {
    if (!cache || !parentApiRef?.current) return;

    const clearCache = () => {
      cache.clear();
    };

    // Store callback in parent apiRef so handleRefresh can call it
    const apiRefAny = parentApiRef.current as any;

    // Initialize the callbacks array if it doesn't exist
    if (!apiRefAny.clearNestedCachesCallbacks) {
      apiRefAny.clearNestedCachesCallbacks = [];
      // Create the main clear function that calls all registered callbacks
      apiRefAny.clearNestedCaches = () => {
        apiRefAny.clearNestedCachesCallbacks?.forEach((cb: () => void) => cb());
      };
    }

    // Check if a callback for this cache already exists to avoid duplicates
    const hasExistingCallback = apiRefAny.clearNestedCachesCallbacks.some(
      (cb: () => void) => cb.toString() === clearCache.toString()
    );

    if (!hasExistingCallback) {
      // Register this cache clearing callback
      apiRefAny.clearNestedCachesCallbacks.push(clearCache);
    }
  }, [parentApiRef, cache]);

  // Generate columns from data if not provided
  const displayColumns: GridColDef[] =
    columns ||
    (data.length > 0
      ? Object.keys(data[0] || {}).map((key) => {
          // Add valueGetter for nested fields (those with dot notation)
          const colDef: GridColDef = {
            field: key,
            headerName: key
              .replace(/\./g, " - ")
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            flex: 1,
            minWidth: 100,
          };

          // If the field has a dot, it's nested - add a valueGetter
          if (key.includes(".")) {
            const [table, column] = key.split(".");
            colDef.valueGetter = (value, row) => row[table]?.[column] ?? null;
          }

          return colDef;
        })
      : []);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { sorting: _unusedSorting, ...restInitialState } =
    initialStateOverride ?? {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
      <Paper
        sx={{
          mx: 2,
          mt: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          flex: 1,
          minHeight: 0,
        }}
      >
        <DataGridPremium
          className="server-data-grid"
          apiRef={apiRef}
          initialState={{ ...restInitialState }}
          rows={data}
          columns={displayColumns}
          columnHeaderHeight={80}
          getRowId={getRowId}
          loading={loading}
          rowSelection={false}
          density="compact"
          hideFooter
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newModel) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(newModel);
          }}
          slots={{ columnMenu: CustomColumnMenu }}
          sx={{
            height: "100%",
            border: "none",
            "& .MuiDataGrid-cell": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      </Paper>
      <TablePagination
        sx={{ width: "100vw" }}
        component="div"
        count={rowCount}
        page={paginationModel.page}
        rowsPerPage={paginationModel.pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_e, newPage) =>
          setPaginationModel((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setPaginationModel({
            page: 0,
            pageSize: parseInt(e.target.value, 10),
          })
        }
      />
    </Box>
  );
};
